/**
 * @module Measurement
 * @description Measurement types
 * add additional Measurement types to this file, extending
 * union types `Measurement` and `MeasurementType`
 */
import type { PositioningMethod } from "building-types/annotation/annotationTypesEnum";
import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { DocNestedEntityTable } from "building-types/doc/docNestedEntityTable";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import { FloorAreaTakeoffType } from "building-types/rooms/takeoffTypes";
import type { PolyLine2Polygon, PolyLine2 } from "geom/polyLine2Types";
import type { FormulaDisabledDocEntityBase } from "./docEntityBase";

export type Measurement = PlanAreaMeasurement;
export type MeasurementType = PlanarMeasurementType;

export type GeneratedMeasurement = GeneratedPlanAreaMeasurement;

/**
 * planar measurement types assume planar context, which could
 * include polygonal areas, brep-based extrusions, view-based
 * dimensions, etc.
 */
export enum PlanarMeasurementType {
  PLANAR_AREA = "PLANAR_AREA",
}

export enum Sign {
  ADDITIVE = "ADDITIVE",
  SUBTRACTIVE = "SUBTRACTIVE",
}

interface MeasurementBase<MeasurementType> extends FormulaDisabledDocEntityBase<"MEASUREMENT"> {
  readonly id: PrimaryEntityId<"MEASUREMENT">;
  readonly measurementType: MeasurementType;
  readonly reportCategories: { [key: string]: boolean };
  readonly sign: Sign;
}

export interface PlanAreaMeasurementShape {
  readonly path: PolyLine2;
  readonly name: string;
}

export type PlanAreaReportCategories = {
  [key: string]: boolean;
};

export const defaultReportCategories: PlanAreaReportCategories = {
  [FloorAreaTakeoffType.CONDITIONED_FLOOR_AREA]: true,
  [FloorAreaTakeoffType.COVERED_PATIO_AREA]: false,
  [FloorAreaTakeoffType.FRONT_PORCH_AREA]: false,
  [FloorAreaTakeoffType.TOTAL_GARAGE_AREA]: false,
  [FloorAreaTakeoffType.UNCONDITIONED_FLOOR_AREA]: false,
  [FloorAreaTakeoffType.UNCOVERED_PATIO_AREA]: false,
  [FloorAreaTakeoffType.REAR_PORCH_AREA]: false,
};

export interface PlanAreaMeasurement extends MeasurementBase<PlanarMeasurementType.PLANAR_AREA> {
  readonly positioningMethod: PositioningMethod;
  readonly parentId: DocEntityParentId<"ROOM">; // enforces optional property on `DocEntityBase`
  readonly parentEdgeId: string;
  readonly reportCategories: PlanAreaReportCategories;
  /**
   * plan area geometry is always in the XY plane and associated with a specific
   * floor level through the parent entity
   */
  readonly shapes: DocNestedEntityTable<"SHAPE", PlanAreaMeasurementShape>;
}

export interface GeneratedPlanAreaMeasurement extends PlanAreaMeasurement {
  readonly union: PolyLine2Polygon[];
  readonly area: number;
}
